/*
 * assets/kcc-cart.js
 * Port of src/components/kona-crown/CartContext.tsx onto the Shopify AJAX Cart API.
 *
 * CartContext gave React: items / isOpen / addItem / removeItem / updateQuantity /
 * toggleCart / closeCart / total / count. The same surface is exposed globally:
 *
 *   window.KCCCart.add({ id, quantity, properties, selling_plan })  -> POST /cart/add.js
 *   window.KCCCart.add(formDataOrFormElement)
 *   window.KCCCart.change({ line | id, quantity })                  -> POST /cart/change.js
 *   window.KCCCart.remove(line)
 *   window.KCCCart.refresh()                                        -> GET  /cart.js
 *   window.KCCCart.open() / .close() / .toggle()
 *   window.KCCCart.getState()   -> last known cart JSON (null until first fetch)
 *   window.KCCCart.formatMoney(cents)
 *
 * Every mutation dispatches on document:
 *   'kcc:cart:updated'  detail = cart JSON
 *   'kcc:cart:error'    detail = { message, data }
 *
 * Submits on form[action$="/cart/add"] are intercepted so PDP forms and product
 * cards both add without a page load. Add data-kcc-no-ajax to opt a form out.
 *
 * Money formatting reads window.KCC.moneyFormat, which theme.liquid sets from
 * {{ shop.money_format | json }}.
 *
 * No libraries. No build step.
 */
(function () {
  'use strict';

  /*
   * layout/theme.liquid publishes window.kcc (moneyFormat, cartCount, strings)
   * and window.routes (Shopify's cart route helpers). window.KCC is kept as an
   * alias so either spelling works.
   */
  var KCC = window.KCC || window.kcc || {};
  window.KCC = KCC;
  if (window.kcc && window.kcc !== KCC) {
    if (!KCC.moneyFormat) KCC.moneyFormat = window.kcc.moneyFormat;
    if (!KCC.strings) KCC.strings = window.kcc.strings;
  }

  /* ---------------------------------------------------------------- routes */

  function rootUrl() {
    var root =
      (KCC.routes && KCC.routes.root) ||
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  /*
   * window.routes gives '/cart', '/cart/add', ... (locale-prefixed on
   * multi-market stores). The AJAX endpoints are those + '.js'.
   */
  function apiUrl(path) {
    var themeRoutes = window.routes || {};
    var map = {
      'cart.js': themeRoutes.cart_url,
      'cart/add.js': themeRoutes.cart_add_url,
      'cart/change.js': themeRoutes.cart_change_url,
      'cart/update.js': themeRoutes.cart_update_url
    };
    if (map[path]) return map[path] + '.js';
    return rootUrl() + path;
  }

  /* ----------------------------------------------------------------- state */

  var state = {
    cart: null,
    open: false,
    lastFocus: null
  };

  /* ----------------------------------------------------------------- utils */

  function q(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function qa(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function escapeHTML(value) {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function emit(name, detail) {
    document.dispatchEvent(
      new CustomEvent(name, { detail: detail, bubbles: true })
    );
  }

  /* ---------------------------------------------------------- money format */

  var MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;

  function withDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents === null || cents === undefined) return '0';
    var number = (cents / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var remainder = parts[1] ? decimal + parts[1] : '';
    return dollars + remainder;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents);

    var formatString = format || KCC.moneyFormat || '${{amount}}';
    var match = formatString.match(MONEY_PLACEHOLDER);
    if (!match) return formatString;

    var value;
    switch (match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return formatString.replace(MONEY_PLACEHOLDER, value);
  }

  /* ------------------------------------------------------------- transport */

  // locales/en.default.json may not carry the cart strings yet, so never show
  // a "Translation missing:" placeholder to a shopper.
  function genericErrorMessage() {
    var fromLocale = KCC.strings && KCC.strings.cartError;
    if (fromLocale && fromLocale.indexOf('Translation missing') === -1) {
      return fromLocale;
    }
    return 'Something went wrong. Please try again.';
  }

  function parseResponse(response) {
    return response
      .json()
      .catch(function () {
        return {};
      })
      .then(function (data) {
        if (!response.ok) {
          var message = data.description || data.message || genericErrorMessage();
          var error = new Error(message);
          error.data = data;
          throw error;
        }
        return data;
      });
  }

  function post(url, body) {
    var options = {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    };

    if (body instanceof FormData) {
      options.body = body;
    } else {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body || {});
    }

    return fetch(url, options).then(parseResponse);
  }

  /* ---------------------------------------------------------------- render */

  function imageUrl(item, width) {
    var src =
      (item.featured_image && item.featured_image.url) || item.image || '';
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /*
   * Mirrors the {% for item in cart.items %} block in snippets/cart-drawer.liquid.
   * Class strings must stay byte-identical to the ones in that file — Tailwind
   * only scans *.liquid, so any class used here has to exist there too.
   */
  function itemHTML(item, index) {
    var line = index + 1;
    var title = escapeHTML(item.product_title);
    var src = imageUrl(item, 160);

    var html = '';
    html += '<div class="flex gap-4" data-kcc-cart-item data-line="' + line + '">';

    html +=
      '<a href="' +
      escapeHTML(item.url) +
      '" class="w-20 h-20 bg-kcc-off-white rounded-sm overflow-hidden shrink-0 block" tabindex="-1" aria-hidden="true">';
    if (src) {
      html +=
        '<img src="' +
        escapeHTML(src) +
        '" alt="' +
        escapeHTML(
          (item.featured_image && item.featured_image.alt) || item.product_title
        ) +
        '" width="80" height="80" loading="lazy" class="w-full h-full object-cover">';
    }
    html += '</a>';

    html += '<div class="flex-1 min-w-0">';
    html +=
      '<a href="' +
      escapeHTML(item.url) +
      '" class="block"><h4 class="text-kcc-forest text-sm font-semibold truncate">' +
      title +
      '</h4></a>';

    if (item.variant_title) {
      html +=
        '<p class="text-kcc-muted text-xs">' +
        escapeHTML(item.variant_title) +
        '</p>';
    }

    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      html +=
        '<p class="text-kcc-gold text-xs font-medium mt-0.5">Subscription &mdash; ' +
        escapeHTML(item.selling_plan_allocation.selling_plan.name) +
        '</p>';
    }

    if (item.properties) {
      Object.keys(item.properties).forEach(function (key) {
        var value = item.properties[key];
        if (!value || key.charAt(0) === '_') return;
        html +=
          '<p class="text-kcc-muted text-xs">' +
          escapeHTML(key) +
          ': ' +
          escapeHTML(value) +
          '</p>';
      });
    }

    html += '<div class="flex items-center justify-between mt-2">';
    html += '<div class="flex items-center border border-kcc-border rounded-sm">';
    html +=
      '<button type="button" data-kcc-line-change data-line="' +
      line +
      '" data-quantity="' +
      (item.quantity - 1) +
      '" class="w-7 h-7 flex items-center justify-center text-kcc-muted hover:text-kcc-forest text-sm" aria-label="Decrease quantity of ' +
      title +
      '">&minus;</button>';
    html +=
      '<span class="w-7 h-7 flex items-center justify-center text-xs text-kcc-forest">' +
      item.quantity +
      '</span>';
    html +=
      '<button type="button" data-kcc-line-change data-line="' +
      line +
      '" data-quantity="' +
      (item.quantity + 1) +
      '" class="w-7 h-7 flex items-center justify-center text-kcc-muted hover:text-kcc-forest text-sm" aria-label="Increase quantity of ' +
      title +
      '">+</button>';
    html += '</div>';

    html += '<div class="flex items-center gap-3">';
    html +=
      '<span class="text-kcc-forest text-sm font-semibold">' +
      formatMoney(item.final_line_price) +
      '</span>';
    html +=
      '<button type="button" data-kcc-line-change data-line="' +
      line +
      '" data-quantity="0" class="text-kcc-muted hover:text-red-500 transition-colors" aria-label="Remove ' +
      title +
      '">';
    html +=
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false"><path d="M2 4H12M5 4V2.5C5 2.22386 5.22386 2 5.5 2H8.5C8.77614 2 9 2.22386 9 2.5V4M5.5 6.5V10.5M8.5 6.5V10.5M3 4L3.5 11.5C3.5 11.7761 3.72386 12 4 12H10C10.2761 12 10.5 11.7761 10.5 11.5L11 4" stroke="currentColor" stroke-width="1" stroke-linecap="round" /></svg>';
    html += '</button>';
    html += '</div>';
    html += '</div>';

    html += '</div>';
    html += '</div>';

    return html;
  }

  function render(cart) {
    if (!cart) return;

    var itemsEl = q('[data-kcc-cart-items]');
    if (itemsEl) {
      itemsEl.innerHTML = cart.items.map(itemHTML).join('');
    }

    var emptyEl = q('[data-kcc-cart-empty]');
    if (emptyEl) emptyEl.hidden = cart.item_count > 0;

    var footerEl = q('[data-kcc-cart-footer]');
    if (footerEl) footerEl.hidden = cart.item_count === 0;

    var subtotalEl = q('[data-kcc-cart-subtotal]');
    if (subtotalEl) subtotalEl.innerHTML = formatMoney(cart.total_price);

    /*
     * Count badges. KCCNavbar only rendered the bubble when count > 0, so any
     * badge that starts life hidden keeps that behaviour. assets/kcc-header.js
     * does the same thing off 'kcc:cart:updated'; doing it here too means the
     * badge is still correct if the header section is swapped out.
     */
    qa('[data-kcc-cart-count]').forEach(function (el) {
      if (!('kccHideEmpty' in el.dataset)) {
        var hidesWhenEmpty =
          el.hidden ||
          el.style.display === 'none' ||
          el.hasAttribute('data-kcc-cart-bubble');
        el.dataset.kccHideEmpty = hidesWhenEmpty ? 'true' : 'false';
      }

      el.textContent = cart.item_count;

      if (el.dataset.kccHideEmpty === 'true') {
        if (cart.item_count > 0) {
          el.hidden = false;
          el.style.display = '';
        } else {
          el.hidden = true;
          el.style.display = 'none';
        }
      }
    });
  }

  function setCart(cart) {
    state.cart = cart;
    KCC.cart = cart;
    render(cart);
    emit('kcc:cart:updated', cart);
    return cart;
  }

  /* ----------------------------------------------------------------- errors */

  function showError(message) {
    var el = q('[data-kcc-cart-error]');
    if (el) {
      el.textContent = message;
      el.hidden = false;
    }
    emit('kcc:cart:error', { message: message });
  }

  function clearError() {
    var el = q('[data-kcc-cart-error]');
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function handleError(error) {
    var message = (error && error.message) || genericErrorMessage();
    showError(message);
    open();
    throw error;
  }

  /* ------------------------------------------------------------ open/close */

  function applyState(el, isOpen) {
    if (!el) return;
    var openClasses = (el.getAttribute('data-open-class') || '').split(/\s+/);
    var closedClasses = (el.getAttribute('data-closed-class') || '').split(/\s+/);
    var add = isOpen ? openClasses : closedClasses;
    var remove = isOpen ? closedClasses : openClasses;

    remove.forEach(function (cls) {
      if (cls) el.classList.remove(cls);
    });
    add.forEach(function (cls) {
      if (cls) el.classList.add(cls);
    });
  }

  function open() {
    var panel = q('[data-kcc-cart-panel]');
    var backdrop = q('[data-kcc-cart-backdrop]');
    if (!panel) return;

    if (!state.open) state.lastFocus = document.activeElement;
    state.open = true;

    applyState(backdrop, true);
    applyState(panel, true);
    panel.setAttribute('aria-hidden', 'false');
    if ('inert' in panel) panel.inert = false;
    document.body.style.overflow = 'hidden';

    var closeBtn = q('[data-kcc-cart-close]', panel);
    if (closeBtn) closeBtn.focus();

    emit('kcc:cart:open', state.cart);
  }

  function close() {
    var panel = q('[data-kcc-cart-panel]');
    var backdrop = q('[data-kcc-cart-backdrop]');
    if (!panel) return;

    state.open = false;
    applyState(backdrop, false);
    applyState(panel, false);
    panel.setAttribute('aria-hidden', 'true');
    if ('inert' in panel) panel.inert = true;
    document.body.style.overflow = '';
    clearError();

    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      state.lastFocus.focus();
      state.lastFocus = null;
    }

    emit('kcc:cart:close', state.cart);
  }

  function toggle() {
    if (state.open) {
      close();
    } else {
      open();
    }
  }

  /* -------------------------------------------------------------- mutations */

  function refresh() {
    return fetch(apiUrl('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(parseResponse)
      .then(setCart);
  }

  function add(payload) {
    clearError();

    var body;
    if (payload instanceof HTMLFormElement) {
      body = new FormData(payload);
    } else if (payload instanceof FormData) {
      body = payload;
    } else if (typeof payload === 'number' || typeof payload === 'string') {
      body = { id: payload, quantity: 1 };
    } else {
      body = Object.assign({ quantity: 1 }, payload || {});
    }

    return post(apiUrl('cart/add.js'), body)
      .then(function () {
        return refresh();
      })
      .then(function (cart) {
        open(); // CartContext.addItem() opened the drawer on every add
        return cart;
      })
      .catch(handleError);
  }

  function change(options) {
    clearError();
    options = options || {};

    var body = { quantity: Number(options.quantity) };
    if (options.line !== undefined && options.line !== null) {
      body.line = Number(options.line);
    } else if (options.id !== undefined) {
      body.id = options.id;
    }
    if (options.properties) body.properties = options.properties;
    if (options.selling_plan) body.selling_plan = options.selling_plan;

    return post(apiUrl('cart/change.js'), body)
      .then(setCart)
      .catch(handleError);
  }

  function remove(line) {
    return change({ line: line, quantity: 0 });
  }

  /* ---------------------------------------------------------------- events */

  function setBusy(el, busy) {
    if (!el) return;
    if (busy) {
      el.setAttribute('aria-busy', 'true');
      el.setAttribute('disabled', 'disabled');
    } else {
      el.removeAttribute('aria-busy');
      el.removeAttribute('disabled');
    }
  }

  function eventTarget(event) {
    var target = event.target;
    return target && typeof target.closest === 'function' ? target : null;
  }

  /*
   * assets/kcc-header.js binds the header cart link itself and asks, via a
   * cancelable 'kcc:cart:toggle', whether a drawer exists. Answering it with
   * preventDefault() keeps the shopper on the page; with no cart JS at all the
   * link just navigates to /cart. The element listener runs before this
   * document-level delegate, so the flag stops the same click toggling twice.
   */
  var toggleHandled = false;

  document.addEventListener('kcc:cart:toggle', function (event) {
    event.preventDefault();
    toggleHandled = true;
    setTimeout(function () {
      toggleHandled = false;
    }, 0);
    toggle();
    if (!state.cart) refresh();
  });

  document.addEventListener('click', function (event) {
    var target = eventTarget(event);
    if (!target) return;

    var openTrigger = target.closest(
      '[data-kcc-cart-open], [data-kcc-cart-toggle]'
    );
    if (openTrigger) {
      // Let cmd/ctrl/shift-click open /cart in a new tab.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();

      if (toggleHandled) {
        toggleHandled = false;
        return;
      }

      if (openTrigger.hasAttribute('data-kcc-cart-toggle')) {
        toggle();
      } else {
        open();
      }
      if (!state.cart) refresh();
      return;
    }

    var closeTrigger = target.closest('[data-kcc-cart-close]');
    if (closeTrigger) {
      // "Browse our coffee" is a real link, let it navigate; just close first.
      if (closeTrigger.tagName !== 'A') event.preventDefault();
      close();
      return;
    }

    if (target.closest('[data-kcc-cart-backdrop]')) {
      event.preventDefault();
      close();
      return;
    }

    var lineTrigger = target.closest('[data-kcc-line-change]');
    if (lineTrigger) {
      event.preventDefault();
      setBusy(lineTrigger, true);
      change({
        line: lineTrigger.getAttribute('data-line'),
        quantity: lineTrigger.getAttribute('data-quantity')
      })
        .catch(function () {
          /* surfaced in the drawer already */
        })
        .then(function () {
          setBusy(lineTrigger, false);
        });
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && state.open) close();
  });

  // PDP forms and product cards: {% form 'product' %} renders action="/cart/add".
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.hasAttribute('data-kcc-no-ajax')) return;

      var action = form.getAttribute('action') || '';
      if (!/\/cart\/add$/.test(action.split('?')[0])) return;

      event.preventDefault();

      var submitButton =
        form.querySelector('[type="submit"]') || form.querySelector('button');
      setBusy(submitButton, true);

      add(new FormData(form))
        .catch(function () {
          /* surfaced in the drawer already */
        })
        .then(function () {
          setBusy(submitButton, false);
        });
    },
    false
  );

  // Cart page quantity inputs (sections/main-cart.liquid). No JS => the visible
  // "Update cart" button still works; with JS the form just submits on change.
  document.addEventListener('change', function (event) {
    var target = eventTarget(event);
    if (!target) return;
    var input = target.closest('[data-kcc-qty-input]');
    if (!input) return;
    var form = input.form;
    if (!form) return;
    if (form.requestSubmit) {
      form.requestSubmit();
    } else {
      form.submit();
    }
  });

  // Cart page steppers: adjust the sibling number input, then let 'change' fire.
  document.addEventListener('click', function (event) {
    var target = eventTarget(event);
    if (!target) return;
    var step = target.closest('[data-kcc-qty-step]');
    if (!step) return;
    event.preventDefault();
    var input = document.getElementById(step.getAttribute('aria-controls'));
    if (!input) return;
    var next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.getAttribute('data-kcc-qty-step')));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Back/forward cache can restore a stale cart; re-sync.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refresh();
  });

  /* ------------------------------------------------------------------ init */

  document.addEventListener('DOMContentLoaded', function () {
    var panel = q('[data-kcc-cart-panel]');
    if (panel && 'inert' in panel) panel.inert = true;
    if (KCC.cart) state.cart = KCC.cart;
  });

  window.KCCCart = {
    add: add,
    change: change,
    remove: remove,
    refresh: refresh,
    open: open,
    close: close,
    toggle: toggle,
    getState: function () {
      return state.cart;
    },
    isOpen: function () {
      return state.open;
    },
    formatMoney: formatMoney
  };
})();
